import { memo, ReactElement, useCallback, useMemo, useRef } from 'react';
import { Button, Flex, Space, Tooltip } from 'antd';
import { CheckCircleOutlined, DeleteOutlined, EditOutlined, EyeOutlined, FormOutlined, ShareAltOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import { Table, TableComponent, TableProps, notification, CrudMode } from '@ohpen/enso-ui';
import { ActionEntityType, ActionStatus, ChangeRequestResponse, FieldType } from '@pmp/api';
import { useConfig } from '@ohpen/react-utils';
import { useAuth } from '@ohpen/react-auth';
import { useListTableState } from '@pmp/hooks';
import { getFilteredEntityTypeList, actionEntityTypeToPermissions, getActionStatusTagMappings } from '@pmp/utils';
import { Guard, useAuthz, useGuard } from '@ohpen/react-authz';

import { columns } from '../tableConfig';
import { ChangeRequestsListProps, FlatChangeRequestData } from '../interfaces';

const canReviewStatus = new Set<ActionStatus>([ActionStatus.PENDING_REVIEW]);
const canEditStatus = new Set<ActionStatus>([ActionStatus.PENDING_REVIEW, ActionStatus.APPROVED, ActionStatus.PENDING_RESUBMISSION]);
const canDeleteStatus = new Set<ActionStatus>([ActionStatus.PENDING_REVIEW, ActionStatus.APPROVED, ActionStatus.PENDING_RESUBMISSION]);
const canApplyStatus = new Set<ActionStatus>([ActionStatus.APPROVED]);
const finalStatus = new Set<ActionStatus>([ActionStatus.APPLIED, ActionStatus.PARTIALLY_APPLIED, ActionStatus.NOT_APPLIED]);

const rowKey = 'actionId';
// List of entities with change requests. Should match with getColumnsByEntityType
const entityTypes = [
  ActionEntityType.FIRMS,
  ActionEntityType.LENDERS,
  ActionEntityType.BROKERS,
  ActionEntityType.PROCURATION_FEES,
  ActionEntityType.PROCURATION_FEE_RULES,
  ActionEntityType.DEFAULT_PROCURATION_FEE_ASSIGNMENTS,
  ActionEntityType.FIRM_PROCURATION_FEE_ASSIGNMENTS,
  ActionEntityType.PRODUCTS,
  ActionEntityType.BASE_RATES,
  ActionEntityType.INTEREST_RATES,
  ActionEntityType.EXCHANGE_RATES,
  ActionEntityType.ERC_PROFILES,
  ActionEntityType.FEES,
  ActionEntityType.FEE_PROFILES,
  ActionEntityType.EXCLUSIVITY_SETS,
];

export const ChangeRequestsList = memo(
  ({ state, valueLists, onView, onReview, onEdit, onDelete, onApply }: ChangeRequestsListProps): ReactElement => {
    const tableRef = useRef<TableComponent | null>(null);

    const tableState = useListTableState<ChangeRequestResponse>('CHANGE_REQUESTS', rowKey, tableRef, columns);

    const { STAGE: stage } = useConfig();

    const { userName } = useAuth();

    const { t } = useTranslation();
    const isDev = stage === 'dev';
    const { allowed: canSelfReview } = useGuard({
      permissions: ['changeRequests:self_review'],
    });
    const { userPermissions } = useAuthz();

    const onRequestView = useCallback(
      (record: ChangeRequestResponse) => {
        if (!record.actionId || !record.actionEntityType.key || !record.status.key) return;
        const mode = finalStatus.has(record.status.key as ActionStatus) ? CrudMode.VIEW_RESULT : CrudMode.VIEW;
        onView(record, mode);
      },
      [onView],
    );

    const onRequestReview = useCallback(
      (record: ChangeRequestResponse) => {
        if (!record.actionId || !record.actionEntityType.key) return;
        onReview(record);
      },
      [onReview],
    );

    const onCopyLink = useCallback(
      (record: ChangeRequestResponse) => {
        if (!record.actionId || !record.actionEntityType.key) return;
        const url = `${globalThis.location.origin}/change-requests?search-change-requests=${encodeURIComponent(record.actionId)}`;
        navigator.clipboard.writeText(url).then(() => {
          notification().success({ title: t('global.notifications.linkCopied') });
        });
      },
      [t],
    );

    const onRequestEdit = useCallback(
      (record: ChangeRequestResponse) => {
        if (!record.actionId || !record.actionEntityType.key) return;
        onEdit(record);
      },
      [onEdit],
    );

    const getRowActions: (record: ChangeRequestResponse) => ReactElement[] = useCallback(
      record => {
        if (!record.status.key) return [];

        const isOwner = record.createdBy === userName;
        const canReview = canReviewStatus.has(record.status.key as ActionStatus) && (!isOwner || isDev || canSelfReview);
        const canEdit = canEditStatus.has(record.status.key as ActionStatus) && isOwner && record.actionType.key !== 'DELETE';
        const canDelete = canDeleteStatus.has(record.status.key as ActionStatus) && isOwner;
        const entityType = actionEntityTypeToPermissions[record.actionEntityType.key as ActionEntityType];
        const canApply = canApplyStatus.has(record.status.key as ActionStatus) && (isOwner || userPermissions.has(`${entityType}:review`));
        const isViewResult = finalStatus.has(record.status.key as ActionStatus);

        const rowActions = [
          <Tooltip key="view" title={t(isViewResult ? 'global.buttons.viewResult' : 'enso.buttons.view')} placement="top">
            <Button
              aria-label="View change request"
              type="text"
              icon={<EyeOutlined />}
              onClick={() => onRequestView(record)}
              disabled={state.crudTable.isOpen || state.deleteModal.open}
            />
          </Tooltip>,
          <Tooltip key="share" title={t('global.buttons.copyLink')} placement="top">
            <Button
              aria-label="Copy link to change request"
              type="text"
              icon={<ShareAltOutlined />}
              onClick={() => onCopyLink(record)}
              disabled={state.crudTable.isOpen || state.deleteModal.open}
            />
          </Tooltip>,
          <Guard key="review" permissions={[`${entityType}:review`]}>
            <Tooltip title={t('global.buttons.review')} placement="top">
              <Button
                aria-label="Review change request"
                type="text"
                icon={<FormOutlined />}
                onClick={() => onRequestReview(record)}
                disabled={!canReview || state.crudTable.isOpen || state.deleteModal.open}
              />
            </Tooltip>
          </Guard>,
          <Guard key="edit" permissions={[`${entityType}:update`]}>
            <Tooltip title={t('enso.buttons.edit')} placement="top">
              <Button
                aria-label="Edit change request"
                type="text"
                icon={<EditOutlined />}
                onClick={() => onRequestEdit(record)}
                disabled={!canEdit || state.crudTable.isOpen || state.deleteModal.open}
              />
            </Tooltip>
          </Guard>,
          <Guard key="apply" permissions={[`${entityType}:update`, `${entityType}:review`]} permissionsOperator="OR">
            <Tooltip title={t('enso.buttons.apply')} placement="top">
              <Button
                aria-label="Apply change request"
                type="text"
                icon={<CheckCircleOutlined />}
                onClick={() => onApply(record)}
                disabled={!canApply || state.crudTable.isOpen || state.deleteModal.open}
              />
            </Tooltip>
          </Guard>,
          <Guard key="delete" permissions={[`${entityType}:update`]}>
            <Tooltip title={t('enso.labels.delete')} placement="top">
              <Button
                type="text"
                aria-label="Delete change request"
                icon={<DeleteOutlined />}
                onClick={() => onDelete(record)}
                disabled={!canDelete || state.crudTable.isOpen || state.deleteModal.open}
              />
            </Tooltip>
          </Guard>,
        ];
        return rowActions;
      },
      [
        userName,
        userPermissions,
        isDev,
        canSelfReview,
        t,
        state.crudTable.isOpen,
        state.deleteModal.open,
        onRequestView,
        onCopyLink,
        onRequestReview,
        onRequestEdit,
        onApply,
        onDelete,
      ],
    );

    const tableProps: TableProps<ChangeRequestResponse> = useMemo(() => {
      const flatDataSource: FlatChangeRequestData[] = state.dataSource.map(item => ({
        ...item,
        timeOfReview: item.reviewInfo?.timeOfReview || null,
        reviewedBy: item.reviewInfo?.reviewedBy || null,
      }));
      const filteredEntitiesValueLists = {
        ...valueLists,
        [FieldType.ACTION_ENTITY_TYPE]: getFilteredEntityTypeList(valueLists, entityTypes),
      };

      return {
        ...tableState,
        ref: tableRef,
        id: 'change-requests',
        itemsLabel: t('global.labels.changeRequests'),
        dataSource: flatDataSource,
        columns,
        tagMappings: getActionStatusTagMappings(t),
        rowKey,
        pagination: true,
        scroll: { x: 'max-content', y: 'table-height' },
        resizableColumns: true,
        valueLists: filteredEntitiesValueLists,
        loading: state.isLoading,
        className: 'flat-table',
        getRowActions,
      };
    }, [tableState, getRowActions, state.dataSource, state.isLoading, t, valueLists]);

    return (
      <Table {...tableProps}>
        <Table.ActionsBar>
          <Flex justify="space-between">
            <Space size="middle">
              <Table.GlobalSearch />
            </Space>
            <Space size="middle">
              <Table.ResetFiltersButton />
              <Table.SelectColumnsButton />
            </Space>
          </Flex>
        </Table.ActionsBar>
      </Table>
    );
  },
);
